import { useEffect, useMemo, useState } from 'react';

import { RegistryPackage, RegistryPackageType } from '@/graphql/upbound-graphql';
import { PackageTabs } from '@/utils/constants/tabs';

import TabContentComps from './__components__/TabContentComps';
import TabContentCrds from './__components__/TabContentCrds';
import TabContentDocs from './__components__/TabContentDocs';
import TabContentXrds from './__components__/TabContentXrds';
import TabTriggerLink from './__components__/TabTriggerLink';

interface TabProps {
  index: number;
  data: RegistryPackage;
  requestedTabType: PackageTabs | undefined;
}

function createTab(
  type: DataTab['type'],
  index: DataTab['index'],
  Link: DataTab['Link'],
  Content: DataTab['Content'],
  hidden?: DataTab['hidden'],
): DataTab {
  return {
    type,
    index,
    Link,
    Content,
    hidden,
  };
}

function createDocsTab({ index, data }: TabProps) {
  return createTab(
    PackageTabs.Docs,
    index,
    props => (
      <TabTriggerLink data={data} append="/docs" {...props}>
        Docs
      </TabTriggerLink>
    ),
    props => <TabContentDocs data={data} {...props} />,
    !data.docs,
  );
}

function createXrdsTab({ index, data }: TabProps) {
  return createTab(
    PackageTabs.XRDs,
    index,
    props => (
      <TabTriggerLink data={data} append="/xrds" className="xrds-tab" {...props}>
        XRDs ({data.resources.length})
      </TabTriggerLink>
    ),
    props => <TabContentXrds data={data} {...props} />,
  );
}

function createCrdsTab({ index, data }: TabProps) {
  return createTab(
    PackageTabs.CRDs,
    index,
    props => (
      <TabTriggerLink data={data} append="/crds" className="provider-crds-tab" {...props}>
        CRDs ({data.resources.length})
      </TabTriggerLink>
    ),
    props => <TabContentCrds data={data} {...props} />,
  );
}

function createCompositionsTab({ index, data }: TabProps) {
  return createTab(
    PackageTabs.Compositions,
    index,
    props => (
      <TabTriggerLink data={data} append="/compositions" className="composition-tab" {...props}>
        Compositions ({data.compositions?.length || 0})
      </TabTriggerLink>
    ),
    props => <TabContentComps data={data} {...props} />,
  );
}

function createTabs(props: Omit<TabProps, 'index'>, ...getTabFns: Array<(props: TabProps) => DataTab>) {
  return getTabFns.reduce<{ allTabs: Array<DataTab>; requestedTab: DataTab | undefined }>(
    ({ allTabs, requestedTab }, getTab, index) => {
      const currentTab = getTab({ ...props, index });
      const nextTabs = [...allTabs, currentTab];

      if (currentTab.type === props.requestedTabType) {
        return { allTabs: nextTabs, requestedTab: currentTab };
      }

      return { allTabs: nextTabs, requestedTab };
    },
    { allTabs: [], requestedTab: undefined },
  );
}

function getAllTabs(props: Omit<TabProps, 'index'>) {
  switch (props.data.packageType) {
    case RegistryPackageType.Configuration: {
      return createTabs(props, createDocsTab, createXrdsTab, createCompositionsTab);
    }
    case RegistryPackageType.Provider: {
      return createTabs(props, createDocsTab, createCrdsTab);
    }
  }
}

function getDefaultTab(props: ReturnType<typeof getAllTabs>) {
  if (props.requestedTab) {
    return props.requestedTab;
  }

  const [firstTab] = props.allTabs;

  if (firstTab.hidden) {
    const firstVisibleTab = props.allTabs.find(tab => !tab.hidden);

    if (!firstVisibleTab) {
      throw new Error('No visible tab groups to choose from');
    }

    return firstVisibleTab;
  }

  return firstTab;
}

function useTabs({ data, requestedTabType }: Omit<TabProps, 'index'>) {
  const { allTabs, requestedTab } = useMemo(() => getAllTabs({ data, requestedTabType }), [data, requestedTabType]);
  const defaultTab = getDefaultTab({ allTabs, requestedTab });
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    setActiveTab(getDefaultTab({ allTabs, requestedTab }));
  }, [allTabs, requestedTab]);

  return { allTabs, requestedTab, defaultTab, activeTab, setActiveTab };
}

export default useTabs;
